import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { formatDateTime } from "../lib/format";
import { Hospital, Search } from "lucide-react";

const ACTIONS = {
  creazione: ["Creazione", "text-emerald-700 bg-emerald-50"],
  modifica: ["Modifica", "text-violet-700 bg-violet-50"],
  cambio_stato: ["Cambio stato", "text-amber-700 bg-amber-50"],
  trasporto_creato: ["Trasporto creato", "text-sky-700 bg-sky-50"],
};
const STATO = { ricevuta: "Ricevuta", presa_in_carico: "Presa in carico", assegnata: "Assegnata" };

export default function StruttureLogPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [struttura, setStruttura] = useState("all");
  const [action, setAction] = useState("all");
  const [code, setCode] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (struttura !== "all") params.struttura = struttura;
    if (action !== "all") params.action = action;
    if (code.trim()) params.code = code.trim();
    const res = await api.get("/strutture/log", { params });
    setData(res.data);
  }, [struttura, action, code]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in pb-10">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2"><Hospital className="h-7 w-7 text-primary" /> Registro Portale Strutture</h1>
        <p className="text-sm text-slate-500 mb-6">Ogni richiesta delle strutture e i suoi cambi di stato, con data, ora e utente.</p>
        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <Select value={struttura} onValueChange={setStruttura}>
              <SelectTrigger data-testid="srlog-filter-struttura" className="h-9 w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le strutture</SelectItem>
                {(data?.strutture || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="srlog-filter-action" className="h-9 w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le azioni</SelectItem>
                {Object.entries(ACTIONS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="srlog-filter-code" placeholder="Codice STR-..." value={code} onChange={(e) => setCode(e.target.value)} className="h-9 pl-8 w-44" />
            </div>
            <span data-testid="srlog-count" className="ml-auto text-xs text-slate-400">{data ? `${data.items.length} azioni` : ""}</span>
          </div>
          {data === null ? (
            <div className="p-5 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-10 w-full" />)}</div>
          ) : data.items.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-500">Nessuna azione registrata.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">Data / Ora</TableHead><TableHead>Utente</TableHead><TableHead>Azione</TableHead>
                    <TableHead>Richiesta</TableHead><TableHead>Struttura</TableHead><TableHead>Dettagli</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((a) => {
                    const [label, cls] = ACTIONS[a.action] || [a.action, "text-slate-700 bg-slate-100"];
                    return (
                      <TableRow key={a.id} data-testid="srlog-row">
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(a.at)}</TableCell>
                        <TableCell className="text-sm font-medium text-slate-800">{a.by_name} {a.by_role !== "admin" && <span className="text-[10px] text-slate-400 font-normal">({a.by_role})</span>}</TableCell>
                        <TableCell><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${cls}`}>{label}</span></TableCell>
                        <TableCell><button type="button" className="font-mono text-xs font-semibold text-primary hover:underline" onClick={() => navigate(`/operativa/strutture/richieste/${a.sr_id}`)}>{a.code}</button></TableCell>
                        <TableCell className="text-xs text-slate-600">{a.struttura}</TableCell>
                        <TableCell className="text-xs text-slate-600">{a.details}{a.to_stato && <span className="text-slate-400"> · {a.from_stato ? `${STATO[a.from_stato]} → ` : ""}{STATO[a.to_stato]}</span>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
